import React, { Fragment, useState, useMemo, useRef, useEffect } from 'react';
import { useApolloClient } from '@apollo/client';
import NumberInput from '~ComponentsImproved/Numpad';
import { LocalVariation } from '~Types';
import { ItemFragmentOnItem, OfferFragment } from '../__queries__';
import Details from '../../Inventory/Details';
import { useCurrency, useI18n, useSelector } from '~Hooks';
import { useInventoryUniverse } from '~Components/InventoryUniverse';
import { useEntryFinder } from '~Components/EntryFinder';
import { ITEM_FRAGMENT, OFFER_FRAGMENT } from '../queries';
import Button from './Button';
import Modal from '~ComponentsImproved/Modal';
import ItemModifications from '../../ItemModifications';
import { ItemCategoryMap } from '~Scenes/POS/categories';
import { useCompetition } from '~Providers/CompetitionsProvider';
import MessageInput from '~ComponentsImproved/MessageInput';
import useItemModifications from '~Hooks/ItemModifications/useItemModifications';
import { useStorage } from '~Components/Storage';

export interface ItemButtonProps {
  onAddItem: (
    id: string,
    title: string,
    categoryId: string,
    options: {
      variations: LocalVariation[];
      removedVariations: LocalVariation[];
      message?: string | null;
      type?: string;
      count?: number;
      courseType?: any;
      isMessage?: boolean;
      parentId?: string;
    },
    price?: number,
  ) => { cartItemIds: string[] };
  title: string;
  description?: string;
  imageUrl?: string;
  label?: string;
  itemId?: string;
  itemToCategoryMap: ItemCategoryMap;
  type?: string;
  venueId: string;
  color: string;
  manualPrice: boolean;
  volumeSuffix: string | null;
  nrInCart: number;
  count: number;
  buttonWidth: number;
  isScrolling: boolean;
  hasServiceCharge?: boolean;
  price?: number;
}

const ItemButton = ({
  onAddItem,
  title,
  description,
  imageUrl,
  label,
  itemId,
  itemToCategoryMap,
  venueId,
  color,
  type,
  manualPrice,
  volumeSuffix,
  nrInCart,
  count,
  buttonWidth,
  isScrolling,
  hasServiceCharge,
  price,
}: ItemButtonProps) => {
  // Temporary feature flag for the upsell flow
  // Company list: Bores (PROD),  Eggs (PROD), 1889 (PROD), Oh Poké (DEV), Vigårda (DEV), Bores (DEV), Godisbaren (dev)
  const inventory = useInventoryUniverse();
  const timeoutRef = useRef<number | null>(null);
  const { i18n } = useI18n();
  const [touching, setTouching] = useState(false);
  const [selected, setSelected] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [showCustomNameInput, setShowCustomNameInput] = useState(false);
  const [customName, setCustomName] = useState('');
  const [showPriceInput, setShowPriceInput] = useState(false);
  const [showVolumeInput, setShowVolumeInput] = useState(false);
  const findEntryFromItem = useEntryFinder();
  const competitions = useCompetition();
  const currency = useCurrency();
  const [storage] = useStorage();

  const isPartOfCompetition = useMemo(() => {
    if (!itemId) return false;
    const category = itemToCategoryMap[itemId];

    if (competitions.specificSales.items.includes(itemId)) {
      return true;
    } else if (competitions.specificSales.categories.includes(category?.id ?? '')) {
      return true;
    } else {
      return false;
    }
  }, [itemId, itemToCategoryMap, competitions.specificSales]);

  const inventoryStatus = useMemo(() => {
    const item = inventory.items.find((i) => i.itemId === itemId);

    // If we found an inventory event for the item or/and category
    // compose an object of the inventory status
    if (item) {
      return {
        count: item.count,
        outOfStock: item.outOfStock,
      };
    }

    return {
      count: null,
      outOfStock: false,
    };
  }, [inventory]);

  const hasAvailableInventory = inventoryStatus.count ? inventoryStatus.count >= count : true;

  const client = useApolloClient();

  const item = useMemo<ItemFragmentOnItem | null>(() => {
    if (!itemId) return null;

    try {
      return client.readFragment<ItemFragmentOnItem>({
        id: `Item${itemId}`,
        fragment: ITEM_FRAGMENT,
      });
    } catch (e) {
      console.error(e);
      return null;
    }
  }, [itemId]);

  const itemModifications = useItemModifications(item?.modificationGroups ?? []);

  const addItem = () => {
    if (!item) {
      console.error('Item fragment missing');
      return;
    }

    /**
     * Do this item have variations in groups that should trigger a popup when adding to cart?
     * Will show the ItemModifications.tsx component if so.
     */
    const shouldShowModificationPopup =
      itemModifications.modificationGroups
        .filter(
          (mg) => mg.expandedByDefault || (mg.required && mg.preselectedVariations.length === 0),
        )
        .flatMap((mg) => mg.variations).length > 0;

    if (shouldShowModificationPopup || item.upsales?.length > 0) {
      if (!selected) {
        setSelected(true);
      }
    } else if (itemId) {
      let itemTitle = title;
      let isMessage = false;

      if (title.includes('[m]')) {
        itemTitle = title.substring(3);
        isMessage = true;
      }

      onAddItem(itemId, itemTitle, itemToCategoryMap[item.id]?.id ?? '', {
        type,
        //@ts-ignore
        categoryId: itemToCategoryMap[item.id],
        variations: [],
        removedVariations: itemModifications.removedVariations,
        courseType: item.courseType,
        isMessage,
        defaultOrderStatus: item.defaultOrderStatus,
      });
      setSelected(false);
    } else {
      console.error('No selection?');
    }
  };

  const isServiceChargeItem = i18n(item?.titleLang)
    .toLowerCase()
    .includes('service charge');

  const serviceChargeSelected = isServiceChargeItem && nrInCart > 0;

  const isOutOfStock =
    inventoryStatus.outOfStock ||
    Boolean(inventoryStatus.count && nrInCart >= inventoryStatus.count) ||
    inventoryStatus.count === 0;

  const disableButton =
    (isServiceChargeItem && hasServiceCharge) || isOutOfStock || serviceChargeSelected;

  const handleTouchStart = () => {
    if (!disableButton) {
      setTouching(true);
    }

    timeoutRef.current = setTimeout(() => {
      if (!isServiceChargeItem) {
        setShowDetails(true);
        setTouching(false);
      }
    }, 500);
  };

  useEffect(() => {
    if (isScrolling) {
      setTouching(false);
      clearTimeout(timeoutRef.current!);
    }
  }, [isScrolling]);

  const handleTouchEnd = () => {
    if (touching) {
      const isCustomNameItem = item?.manualName;

      if (
        hasAvailableInventory &&
        !disableButton &&
        !manualPrice &&
        !isCustomNameItem &&
        !volumeSuffix &&
        !serviceChargeSelected
      ) {
        addItem();
      }

      if (isCustomNameItem) {
        setShowCustomNameInput(true);
      }

      if (manualPrice) {
        setShowPriceInput(true);
      } else if (volumeSuffix) {
        setShowVolumeInput(true);
      }

      clearTimeout(timeoutRef.current!);
      setTouching(false);
    }
  };

  const addItemWithPrice = (price: number, customName?: string) => {
    setShowPriceInput(false);
    setCustomName('');

    if (!item) {
      console.error('Item fragment missing');
      return;
    }

    if (itemId) {
      onAddItem(
        itemId,
        title,
        itemToCategoryMap[item.id]?.id ?? '',
        {
          variations: [],
          removedVariations: [],
          courseType: item.courseType,
          message: customName ? customName : undefined,
        },
        price,
      );
    }
  };

  const addItemWithCustomName = (name: string) => {
    setShowCustomNameInput(false);
    setCustomName('');

    if (!item) {
      console.error('Item fragment missing');
      return;
    }

    if (itemId) {
      onAddItem(itemId, title, itemToCategoryMap[item.id]?.id ?? '', {
        variations: [],
        removedVariations: [],
        courseType: item.courseType,
        message: name,
      });
    }
  };

  /**
   * Adds an item to cart based on a certain volume or weight.
   * For example when buying drinks by the CL or a salad by the kg.
   *
   * @param amount How many cl/kg etc to add to the cart
   */
  const addItemWithVolume = (amount: number, customName?: string) => {
    setShowVolumeInput(false);
    setShowCustomNameInput(false);
    setCustomName('');

    if (!item) {
      console.error('Item fragment missing');
      return;
    }

    if (!itemId) {
      console.error('Missing item ID - How did this happen?');
      return;
    }

    // We change the title of the product so we can send a descriptive bong
    // to the kitchen and easily manage them in the cart.
    // For example, buying two shots of 4cl tequila tequila would show "2x 4cl Tequila" on the receipt and the bong.
    const titleWithVolume = `${amount}${volumeSuffix} ${title}`;

    // Get the entry so we can get the price
    const entry = findEntryFromItem(itemId);

    if (entry) {
      // We multiply the amount/weight with the price to get the final price
      const price = amount * entry.price;

      onAddItem(
        itemId,
        titleWithVolume,
        itemToCategoryMap[item.id]?.id ?? '',
        {
          variations: [],
          removedVariations: [],
          courseType: item.courseType,
          message: customName ? customName : undefined,
        },
        price,
      );
    }
  };

  const close = () => {
    setSelected(false);
  };

  // Conditionally show price based on storage setting
  const displayPrice =
    storage.showPricesOnButtons && price ? `(${currency.format(price)})` : undefined;

  return (
    <Fragment key={itemId}>
      <Fragment>
        <Button
          touching={touching}
          color={color}
          itemId={itemId ?? ''}
          disableButton={disableButton}
          handleTouchStart={handleTouchStart}
          handleTouchEnd={handleTouchEnd}
          buttonWidth={buttonWidth}
          hasAvailableInventory={hasAvailableInventory}
          title={label || title}
          subTitle={displayPrice}
          volumeSuffix={volumeSuffix}
          nrInCart={nrInCart}
          inventoryStatus={inventoryStatus}
          isPartOfCompetition={isPartOfCompetition}
          imageUrl={imageUrl}
        />

        <Modal isShowing={selected} backdropClose={false}>
          <ItemModifications
            item={item}
            onClose={close}
            addItem={({
              id = itemId,
              itemTitle = title,
              courseType,
              variations,
              removedVariations,
              parentId,
            }) =>
              onAddItem(id!, itemTitle, itemToCategoryMap[id!]?.id ?? '', {
                variations,
                removedVariations,
                courseType: courseType ?? item?.courseType,
                parentId,
              })
            }
          />
        </Modal>
        <Modal isShowing={showDetails} backdropClose={true}>
          <Details
            id={itemId!}
            title={title}
            venueId={venueId}
            isOutOfStock={inventoryStatus.outOfStock}
            onCancel={() => setShowDetails(false)}
            defaultValue={inventoryStatus.count ?? 0}
            description={description}
            imageUrl={imageUrl}
          />
        </Modal>
      </Fragment>
      {showCustomNameInput && (
        <Modal
          isShowing={true}
          backdropClose={true}
          hide={() => {
            setCustomName('');
            setShowPriceInput(false);
            setShowCustomNameInput(false);
          }}>
          <MessageInput
            autoFocus={true}
            placeHolder={'Enter custom name'}
            value={customName}
            onSubmit={(value: string) => {
              // If the item also have manual pricing or volume we hide the naming modal and show the pricing/volume modal
              if (showPriceInput || showVolumeInput) {
                setShowCustomNameInput(false);
                return;
              }

              addItemWithCustomName(value);
            }}
            onInput={(value: string) => setCustomName(value)}
            width={undefined}
          />
        </Modal>
      )}

      {showPriceInput && !showCustomNameInput && (
        <NumberInput
          decimal
          title={i18n('POS.EnterPrice')}
          onClose={() => {
            setShowPriceInput(false);
            setCustomName('');
          }}
          onSubmit={(val) => addItemWithPrice(Number(val) * 100, customName)}
        />
      )}

      {showVolumeInput && !showCustomNameInput && (
        <NumberInput
          decimal
          title={`${i18n('POS.HowMany')} ${volumeSuffix}?`}
          onClose={() => {
            setShowVolumeInput(false);
            setCustomName('');
          }}
          onSubmit={(val) => addItemWithVolume(Number(val), customName)}
        />
      )}
    </Fragment>
  );
};

export default React.memo(ItemButton);
